import { computed, reactive, ref, unref, watch } from 'vue'

import api from '@/store/api'

export const PAGE_SIZE = 20

// The Orders sub-tabs and the production status filter each one sends.
export const ORDER_TABS = [
  { id: 'all', name: 'All', filter: null },
  { id: 'unpacked', name: 'Unpacked', filter: 'not_produced' },
  { id: 'packed', name: 'Packed', filter: 'packed' },
  { id: 'cancelled', name: 'Cancelled', filter: 'cancelled' }
]

const ORDER_SORT = 'sort_by_field=bottle_payments.created_at&sort_direction=asc'

// Packing moves a bottle from one sub-tab to the other, so that one's count changes too.
const COUNTERPART = { unpacked: 'packed', packed: 'unpacked' }

const emptyList = () => ({ rows: [], size: null, cursor: 0, nextCursor: null, loading: false })

// Summary and products answer plain JSON. Order and cart lists are JSONAPI and go into the
// object cache, because BottleRow reads each bottle's relationships from it.
const PLAIN = { receive: false }

// One request in flight per section: a new one aborts the old, and only the newest may apply its answer.
const latestPerSection = () => {
  const tokens = {}
  const controllers = {}
  return {
    begin(key) {
      controllers[key]?.abort()
      controllers[key] = new AbortController()
      tokens[key] = (tokens[key] ?? 0) + 1
      return { token: tokens[key], abortController: controllers[key] }
    },
    isCurrent: (key, token) => tokens[key] === token,
    abortAll() {
      Object.keys(controllers).forEach(key => {
        controllers[key].abort()
        tokens[key] = (tokens[key] ?? 0) + 1
      })
    }
  }
}

const locationNameOf = user => user.attributes.user_name || user.attributes.handle || ''

export function useFranchiseDay(date) {
  const day = computed(() => unref(date))
  const base = () => `franchise/production_days/${day.value}`

  const summary = ref(null)
  const products = ref([])
  const loading = reactive({ summary: false, products: false })
  const orders = reactive(Object.fromEntries(ORDER_TABS.map(tab => [tab.id, emptyList()])))
  const carts = reactive(emptyList())
  const locationNames = reactive({})

  const sections = latestPerSection()
  // What has been opened for this date, so a new date reloads just that.
  const opened = new Map()

  // Resolves true when this answer was the newest and got applied, false when a newer request
  // replaced it or it failed. An aborted request never settles (quietAbort).
  const fetchSection = (key, path, options, apply, empty, setLoading) => {
    const { token, abortController } = sections.begin(key)
    const isCurrent = () => sections.isCurrent(key, token)
    const applyIfCurrent = (data, applied) => {
      if (!isCurrent()) return false
      apply(data)
      return applied
    }
    setLoading(true)
    return api
      .call('GET', path, {}, { ...options, abortController, quietAbort: true })
      .then(response => applyIfCurrent(response.data, true))
      .catch(() => applyIfCurrent(empty, false))
      .finally(() => isCurrent() && setLoading(false))
  }

  const rememberLocations = included =>
    (included ?? [])
      .filter(record => record.type === 'user')
      .forEach(user => {
        locationNames[user.id] = locationNameOf(user)
      })

  const fillList = list => data => {
    rememberLocations(data.included)
    list.rows = data.data ?? []
    list.size = data.total_records ?? 0
    list.nextCursor = data.next_cursor ?? null
  }

  const loadList = (key, list, path, cursor) => {
    opened.set(key, () => loadList(key, list, path, 0))
    list.cursor = cursor
    return fetchSection(key, path(cursor), {}, fillList(list), {}, value => (list.loading = value))
  }

  const loadSummary = () => {
    opened.set('summary', loadSummary)
    return fetchSection(
      'summary',
      `${base()}/summary`,
      PLAIN,
      data => (summary.value = data),
      null,
      value => (loading.summary = value)
    )
  }

  const loadProducts = () => {
    opened.set('products', loadProducts)
    return fetchSection(
      'products',
      `${base()}/products`,
      PLAIN,
      data => (products.value = data.products ?? []),
      {},
      value => (loading.products = value)
    )
  }

  const ordersPath = filter => cursor =>
    `${base()}/orders?offset=${PAGE_SIZE}&cursor=${cursor}&${ORDER_SORT}${filter ? `&filter=${filter}` : ''}`
  const cartsPath = cursor => `${base()}/carts?offset=${PAGE_SIZE}&cursor=${cursor}`

  const tabFilter = key => ORDER_TABS.find(tab => tab.id === key).filter

  const loadOrders = (key, cursor = 0) => loadList(`orders:${key}`, orders[key], ordersPath(tabFilter(key)), cursor)
  const nextOrders = key => loadOrders(key, orders[key].nextCursor ?? orders[key].cursor + PAGE_SIZE)
  const prevOrders = key => loadOrders(key, Math.max(0, orders[key].cursor - PAGE_SIZE))

  const loadCarts = (cursor = 0) => loadList('carts', carts, cartsPath, cursor)
  const nextCarts = () => loadCarts(carts.nextCursor ?? carts.cursor + PAGE_SIZE)
  const prevCarts = () => loadCarts(Math.max(0, carts.cursor - PAGE_SIZE))

  const locationName = bottle => locationNames[bottle.relationships?.merchant?.data?.id] ?? ''

  const removeRow = (list, bottle) => {
    const before = list.rows.length
    list.rows = list.rows.filter(row => row.id !== bottle.id)
    if (list.rows.length < before && list.size) list.size -= 1
  }

  // Refetches the page shown, so the row after the page fills the gap a moved bottle left and the
  // pager matches the server. A page emptied by the move steps back one page.
  const reloadOrdersPage = key => {
    const { cursor } = orders[key]
    return loadOrders(key, cursor).then(applied => {
      if (applied && cursor > 0 && !orders[key].rows.length) return loadOrders(key, Math.max(0, cursor - PAGE_SIZE))
    })
  }

  // The lists and counts a packing change touches. Fired, not awaited: a newer request aborts an
  // older one, and an aborted request never settles.
  const refreshAfterPacking = fromKey => {
    reloadOrdersPage(fromKey)
    const counterpart = COUNTERPART[fromKey]
    if (counterpart) loadOrders(counterpart, orders[counterpart].cursor)
    if (opened.has('summary')) loadSummary()
  }

  // A franchise sees every location's bottles, so the write goes out as the bottle's own merchant,
  // never as whichever location happens to be active. Resolves once the write lands.
  const setProductionStatus = (bottle, productionStatus, fromKey) =>
    api
      .call(
        'PUT',
        `bottles/${bottle.id}`,
        { bottle: { production_statuses_attributes: [{ production_status: productionStatus }] } },
        { merchantId: bottle.relationships.merchant.data.id }
      )
      .then(() => {
        bottle.attributes.production_status = productionStatus
        removeRow(orders[fromKey], bottle)
        refreshAfterPacking(fromKey)
      })

  const reset = () => {
    summary.value = null
    products.value = []
    ORDER_TABS.forEach(tab => Object.assign(orders[tab.id], emptyList()))
    Object.assign(carts, emptyList())
  }

  watch(day, newDate => {
    if (!newDate) return
    sections.abortAll()
    reset()
    const reloads = [...opened.values()]
    opened.clear()
    reloads.forEach(reload => reload())
  })

  return {
    date: day,
    summary,
    products,
    loading,
    orders,
    carts,
    locationName,
    loadSummary,
    loadProducts,
    loadOrders,
    nextOrders,
    prevOrders,
    loadCarts,
    nextCarts,
    prevCarts,
    setProductionStatus
  }
}
